import BlogBackButton from "@/components/blog/BlogBackButton";
import BlogPostHeader from "@/components/blog/BlogPostHeader";
import BlogPostNavigation from "@/components/blog/BlogPostNavigation";
import BlogPostSEO from "@/components/blog/BlogPostSEO";
import BlogPostSkeleton from "@/components/blog/BlogPostSkeleton";
import BlogPostSocialShare from "@/components/blog/BlogPostSocialShare";
import ReadingProgressBar from "@/components/blog/ReadingProgressBar";
import useReadingProgress from "@/hooks/useReadingProgress";
import { trackEvent } from "@/utils/analytics";
import { BlogPost as BlogPostType, calculateReadTime, getAdjacentPosts, getBlogPostBySlug } from "@/utils/blogUtils";
import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";

/**
 * Componente BlogPost - Exibe um artigo completo do blog
 */
const BlogPost = () => {
  // Get slug from URL parameters
  const { slug } = useParams<{ slug: string }>();
  
  // States for managing the post and UI
  const [post, setPost] = useState<BlogPostType | null>(null);
  const [loading, setLoading] = useState(true);
  const [prevPost, setPrevPost] = useState<BlogPostType | null>(null);
  const [nextPost, setNextPost] = useState<BlogPostType | null>(null);
  const [notFound, setNotFound] = useState(false);

  // Custom hook for reading progress
  const readingProgress = useReadingProgress({
    trackMilestones: true,
    postTitle: post?.title,
    postId: post?.id
  });

  useEffect(() => {
    // Track page view when post is loaded
    if (post) {
      const numericId = post.id ? Number(post.id) || undefined : undefined;
      trackEvent('Blog', 'PostView', `Post: ${post.title}`, numericId);
    }
  }, [post]);

  useEffect(() => {
    // Reset state when slug changes
    setLoading(true);
    setPost(null);
    setPrevPost(null);
    setNextPost(null);
    setNotFound(false);

    const fetchBlogPost = async () => {
      if (slug) {
        try {
          const foundPost = await getBlogPostBySlug(slug);
          if (foundPost) {
            setPost(foundPost);
            
            // Get adjacent posts for navigation
            const { prev, next } = await getAdjacentPosts(slug);
            setPrevPost(prev);
            setNextPost(next);
          } else {
            setNotFound(true);
            trackEvent('Blog', 'PostNotFound', `Slug: ${slug}`);
          }
        } catch (error) {
          console.error("Error fetching blog post:", error);
          setNotFound(true);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchBlogPost();
  }, [slug]);

  /**
   * Handle click on a post tag
   */
  const handleTagClick = (tag: string, event: React.MouseEvent) => {
    event.preventDefault();
    trackEvent('Blog', 'TagClick', `Tag: ${tag}`);
  };

  /**
   * Handle sharing using Web Share API when available
   */
  const handleShare = async () => {
    if (!post) return;
    
    const numericId = post.id ? Number(post.id) || undefined : undefined;
    trackEvent('Blog', 'Share', `Method: WebShare, Post: ${post.title}`, numericId);
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.meta_description,
          url: `https://www.periciatecnica.eng.br/blog/${post.slug}`,
        });
        trackEvent('Blog', 'ShareSuccess', `Method: WebShare, Post: ${post.title}`, numericId);
      } catch (error) {
        trackEvent('Blog', 'ShareError', `Method: WebShare, Post: ${post.title}`, numericId);
      }
    }
  };

  /**
   * Handle sharing on social platforms
   */
  const handleSocialShare = (platform: string) => {
    if (!post) return;
    
    const numericId = post.id ? Number(post.id) || undefined : undefined;
    trackEvent('Blog', 'Share', `Method: ${platform}, Post: ${post.title}`, numericId);
  };

  /**
   * Handle click to navigate to previous post
   */
  const handlePrevPostClick = () => {
    if (prevPost) {
      const numericId = prevPost.id ? Number(prevPost.id) || undefined : undefined;
      trackEvent('Blog', 'NavigationClick', `Direction: Previous, Post: ${prevPost.title}`, numericId);
    }
  };

  /**
   * Handle click to navigate to next post
   */
  const handleNextPostClick = () => {
    if (nextPost) {
      const numericId = nextPost.id ? Number(nextPost.id) || undefined : undefined;
      trackEvent('Blog', 'NavigationClick', `Direction: Next, Post: ${nextPost.title}`, numericId);
    }
  };

  // Redirect to blog page if post not found
  if (notFound) {
    return <Navigate to="/blog" replace />;
  }

  // Display loading skeleton while fetching post
  if (loading) {
    return <BlogPostSkeleton />;
  }

  // Display post not found message (additional fallback)
  if (!post) {
    return <BlogPostSkeleton notFound={true} />;
  }

  // Current URL for SEO
  const currentUrl = `https://www.periciatecnica.eng.br/blog/${post.slug}`;
  
  // Reading time
  const readTime = post.readTime || calculateReadTime(post.content);

  return (
    <>
      {/* Add SEO metadata with Helmet */}
      <BlogPostSEO post={post} currentUrl={currentUrl} />

      {/* Reading progress bar - fixed at top */}
      <ReadingProgressBar readingProgress={readingProgress} />

      <div className="pt-24 pb-16 bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-black">
        <div className="container mx-auto px-4">
          {/* Back button to blog page */}
          <BlogBackButton />

          {/* Post header - Full width for featured image */}
          <BlogPostHeader post={post} handleTagClick={handleTagClick} readTime={readTime} />

          {/* Post content */}
          <div className="max-w-4xl mx-auto">
            {/* Main article with HTML content */}
            <article 
              className="prose dark:prose-invert max-w-none mt-8 mb-12" 
              dangerouslySetInnerHTML={{ __html: post.content.join('') }}
            />

            {/* Social sharing */}
            <BlogPostSocialShare 
              post={post} 
              handleSocialShare={handleSocialShare} 
            />

            {/* Posts navigation section */}
            <BlogPostNavigation
              prevPost={prevPost}
              nextPost={nextPost}
              handlePrevPostClick={handlePrevPostClick}
              handleNextPostClick={handleNextPostClick}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogPost;
